import { useEffect, useRef, useState } from "react";
import Header from "../../Header";
import PageTransition from "../../../PageTransition";
import "../Branding.css";
import { useNavigate } from "react-router-dom";
import schnellImg from '../../../assets/schnell-builders.webp'
import nycenergycode from '../../../assets/nyc-energy-code.webp'
import lucid from '../../../assets/lucid.webp'
import localLaw from '../../../assets/local-law-97.webp'
import cafeNourished from '../../../assets/cafe-nourished.webp'
import sugarSalt from '../../../assets/sugar-salt-cafe.webp'
import crownMedical from '../../../assets/crown-medical.webp'
import morecardimg from '../../../assets/more-card-img.png'

/* ================= DATA ================= */
const BRANDING_WORKS = [
  {
    title: "NOURISHED CAFE",
    desc: "Fresh coastal wholefood cafe — built with Lovable & custom design.",
    image: cafeNourished,
    link: 'https://nourished-cafe.lovable.app/',
  },
  {
    title: "SUGAR & SALT CAFE",
    desc: "Boutique cafe & brunch destination — built with Lovable & custom design.",
    image: sugarSalt,
    link: 'https://sugar-salt-cafe.lovable.app/',
  },
  {
    title: "CROWN MEDICAL",
    desc: "Healthcare management & ops network — built with Lovable & custom design.",
    image: crownMedical,
    link: 'https://crown-medical-preview.netlify.app/',
  },
  {
    title: "NY ENERGY CODE",
    desc: "NYC Energy Conservation Code compliance resources.",
    image: nycenergycode,
    link: 'https://nycenergycode.com/',
  },
  {
    title: "SCHNELL BROTHERS",
    desc: "Turnkey Construction Solutions",
    image: schnellImg,
    link: 'https://schnellbuilder.com/',
  },
  {
    title: "LOCAL LAW 97 ",
    desc: "Resources for  New york Local Law 97",
    image: localLaw,
    link: 'https://locallaw97guide.com/',
  },
  {
    title: "LUCID MIND ",
    desc: "psychological counseling for individuals and corporate employees.",
    image: lucid,
    link: 'https://lucidmind.in/',
  },
  {
    image: morecardimg
  }
];

/* ================= RESPONSIVE COUNT ================= */
const getVisible = () => {
  if (window.innerWidth <= 768) return 1; // mobile
  return 2; // desktop & tablet (ONLY TWO)
};


export default function Website() {
  const sliderRef = useRef(null);

  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(getVisible());
  const [hint, setHint] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const isAnimating = useRef(false);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const touchStartX = useRef(0);
  const navigate = useNavigate();

  const maxIndex = BRANDING_WORKS.length - visible;

  /* ================= RESET ================= */
  useEffect(() => {
    document.body.style.overflow = "auto";
    window.scrollTo(0, 0);
  }, []);

  /* ================= RESIZE ================= */
  useEffect(() => {
    const onResize = () => {
      const v = getVisible();
      setVisible(v);
      setIsMobile(window.innerWidth <= 768);
      setIndex((i) => Math.min(i, BRANDING_WORKS.length - v));
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* ================= WHEEL (DESKTOP) ================= */
  useEffect(() => {
    if (isMobile) return;

    const onWheel = (e) => {
      e.preventDefault();
      if (isAnimating.current) return;

      if (e.deltaY > 0 && index < maxIndex) slideTo(index + 1);
      if (e.deltaY < 0 && index > 0) slideTo(index - 1);
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, [index, maxIndex, isMobile]);

  /* ================= CURSOR HINT ================= */
  useEffect(() => {
    if (isMobile) return;

    const move = (e) => {
      if (!sliderRef.current) return;
      const r = sliderRef.current.getBoundingClientRect();
      setHint(e.clientX > r.left + r.width / 2 ? "right" : "left");
    };

    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, [isMobile]);

  /* ================= MOUSE DRAG ================= */
  const onMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.clientX;
  };

  const onMouseMove = (e) => {
    if (!isDragging.current || isAnimating.current) return;

    const delta = e.clientX - startX.current;
    if (delta > 80 && index > 0) {
      slideTo(index - 1);
      isDragging.current = false;
    }
    if (delta < -80 && index < maxIndex) {
      slideTo(index + 1);
      isDragging.current = false;
    }
  };

  const onMouseUp = () => {
    isDragging.current = false;
  };

  /* ================= TOUCH ================= */
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e) => {
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) < 60) return;

    if (delta < 0 && index < maxIndex) slideTo(index + 1);
    if (delta > 0 && index > 0) slideTo(index - 1);
  };

  /* ================= TAP ================= */
  const onClickSlide = (e) => {
    if (isMobile || isAnimating.current) return;

    const r = sliderRef.current.getBoundingClientRect();
    const mid = r.left + r.width / 2;

    if (e.clientX > mid && index < maxIndex) slideTo(index + 1);
    if (e.clientX < mid && index > 0) slideTo(index - 1);
  };

  /* ================= TOUCH MOVE ================= */
  const onTouchMove = (e) => {
    if (isAnimating.current) return;
    const touchX = e.touches[0].clientX;
    const delta = touchX - touchStartX.current;
    if (Math.abs(delta) < 30) return;
    if (delta < 0 && index < maxIndex) {
      slideTo(index + 1);
      touchStartX.current = touchX;
    }
    if (delta > 0 && index > 0) {
      slideTo(index - 1);
      touchStartX.current = touchX;
    }
  };

  /* ================= SLIDE ================= */
  const slideTo = (i) => {
    isAnimating.current = true;
    setIndex(i);
    setTimeout(() => (isAnimating.current = false), 700);
  };

  /* ================= OPEN PDF ================= */
  const openPdf = (pdf) => {
    window.open(pdf, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <Header visible mode="normal" />

      <PageTransition>
        <main className="branding-page" style={{ marginTop: isMobile ? "8rem" : "12rem" }}>
          {/* ================= HERO ================= */}
          <section className="branding-hero">
            <h1>UI/UX Design</h1>
            <p>
              We craft high-performing, user-centric digital experiences that turn visitors into loyal customers, Design and development of websites and applications solutions.
            </p>
          </section>

          {/* ================= SLIDER ================= */}
          <div
            ref={sliderRef}
            className="branding-slider"
            onClick={onClickSlide}
            onMouseDown={!isMobile ? onMouseDown : undefined}
            onMouseMove={!isMobile ? onMouseMove : undefined}
            onMouseUp={!isMobile ? onMouseUp : undefined}
            onMouseLeave={!isMobile ? onMouseUp : undefined}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* {hint && (
              <div className={`cursor-hint ${hint}`}>
                {hint === "right" ? "Tap →" : "← Tap"}
              </div>
            )} */}

            <div
              className="branding-track"
              style={{
                transform: `translateX(-${index * (100 / visible)}%)`,
              }}
            >
              {BRANDING_WORKS.map((item, i) => (
                <div className="branding-card" key={i}>
                  <div
                    className="branding-image"
                    style={{ backgroundImage: `url(${item.image})` }}
                  />

                  {item.title && item.link && (
                    <button
                      className="branding-cta"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.link.startsWith("http")) {
                          window.open(item.link, "_blank", "noopener,noreferrer");
                        } else {
                          navigate(item.link);
                        }
                      }}
                    >
                      {item.title}
                    </button>
                  )}

                  {item.desc && <span>{item.desc}</span>}
                </div>
              ))}

            </div>
          </div>
        </main>
      </PageTransition>
    </>
  );
}
